/**
 * PD-17093 — Jobs API shapes, shared by crm-api and the jobs module (sales
 * relationships plan, section 4). A job is the won work delivered to one
 * customer; its orders and quotes link through `order.fk_job__id` and its
 * invoices count through their lines' orders.
 *
 * Request bodies are plain types validated in the service, as the competitor
 * DTOs are, so the global whitelisting ValidationPipe never strips fields.
 */
import type { JobEntity, JobStatus } from '../entity/job.entity';
import type { OpportunityStageOutcome } from '../entity/m-sm-client-opportunity-stage.entity';
import type { JobSuggestionSource } from '../entity/job-suggestion-dismissal.entity';

/** Labels for the job statuses, in their display order. */
export const JOB_STATUS_LABELS: Readonly<Record<JobStatus, string>> = {
	planned: 'Planned',
	active: 'Active',
	on_hold: 'On Hold',
	closed: 'Closed',
};

/** The fields a rep edits on a job. */
export type JobEditableFieldsDto = Pick<
	JobEntity,
	| 'name'
	| 'jobNumber'
	| 'status'
	| 'description'
	| 'fkClientId'
	| 'fkUserIdSalesperson'
	| 'startDate'
	| 'endDate'
	| 'fkOpportunityId'
	| 'fkPriceContractId'
	| 'fkProjectId'
> & {
	/** Decimal returned as a number. */
	estimatedValue: number | null;
};

export interface JobTotalsDto {
	quotedTotal: number;
	orderedTotal: number;
	invoicedTotal: number;
	quoteCount: number;
	orderCount: number;
	invoiceCount: number;
	lastOrderDate: string | null;
	totalsRefreshedDate: string | null;
	/**
	 * PD-17135 — gross profit to date over the lines whose cost is known, and
	 * its margin (0–1). Sent only to users with `orders:gp:view` (the service
	 * leaves them out and the GP response filter strips them besides); null
	 * when no line has a known cost.
	 */
	orderedGp?: number | null;
	orderedGpMargin?: number | null;
	invoicedGp?: number | null;
	invoicedGpMargin?: number | null;
}

/** One row of the Jobs list and a customer's Jobs tab. */
export interface JobListRowDto
	extends JobEditableFieldsDto,
		JobTotalsDto,
		Pick<JobEntity, 'id'> {
	clientName: string;
	salespersonName: string | null;
	opportunityName: string | null;
	projectName: string | null;
	closedDate: string | null;
	createdDate: string;
	/** Ordered to date as a share of the estimated value (0–1); null without an estimate. */
	percentOfEstimate: number | null;
}

export interface JobShipToDto {
	id: number;
	fkClientAddressId: number;
	name: string | null;
	address: string;
}

export interface JobContactDto {
	id: number;
	fkContactId: number;
	name: string;
	title: string | null;
	email: string | null;
	phone: string | null;
	role: string | null;
}

/**
 * PD-17109 — an opportunity on a job (`m_sm_client_opportunity.fk_job__id`):
 * a piece of the job's work being bid or won. A job can hold many.
 */
export interface JobOpportunityDto {
	id: number;
	name: string;
	stageName: string | null;
	/** From the stage: open, won or lost. */
	outcome: OpportunityStageOutcome;
	amount: number | null;
	closeDate: string | null;
	salespersonName: string | null;
	/** The opportunity the job was created from (`job.fk_m_sm_client_opportunity__id`). */
	isCreatedFrom: boolean;
}

/** A job's page: the row plus its ship-tos, people and links. */
export interface JobDto extends JobListRowDto {
	priceContractName: string | null;
	shipTos: Array<JobShipToDto>;
	contacts: Array<JobContactDto>;
	/** PD-17109 — the opportunities on the job, newest first. */
	opportunities: Array<JobOpportunityDto>;
	createdByName: string | null;
	/**
	 * PD-17134 — on a create or update response only: the customer's earlier
	 * orders and quotes this save linked because their job number matched the
	 * job's number or name. The web app offers Undo for them.
	 */
	autoLinkedOrderIds?: Array<number>;
}

/** POST /api/jobs */
export type CreateJobRequestDto = Pick<JobEditableFieldsDto, 'name' | 'fkClientId'> &
	Partial<Omit<JobEditableFieldsDto, 'name' | 'fkClientId'>> & {
		/** Orders or quotes to link at once (a won opportunity's quote, for example). */
		orderIds?: Array<number>;
	};

/** PUT /api/jobs/:id — only the fields sent change. */
export type UpdateJobRequestDto = Partial<Omit<JobEditableFieldsDto, 'fkClientId'>>;

/** An order or quote on a job's page. */
export interface JobOrderRowDto {
	id: number;
	externalId: string | null;
	isQuote: boolean;
	status: string | null;
	statusLabel: string;
	date: string | null;
	total: number;
	poNumber: string | null;
	jobNumber: string | null;
	shipToName: string | null;
}

/** An invoice on a job's page (through its lines' orders). */
export interface JobInvoiceRowDto {
	id: number;
	externalId: string | null;
	date: string | null;
	dueDate: string | null;
	total: number;
	balance: number | null;
	status: string | null;
}

/** A job in a picker (orders and quotes, a won opportunity). */
export interface JobOptionDto {
	id: number;
	name: string;
	jobNumber: string | null;
	status: JobStatus;
	fkClientId: number;
	clientName: string;
}

/** A note on a job (a client note carrying the job's id). */
export interface JobNoteDto {
	id: number;
	note: string;
	dateTime: string;
	createdByName: string | null;
}

export interface JobListResponseDto {
	success: true;
	data: Array<JobListRowDto>;
	pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface JobPagedResponseDto<T> {
	success: true;
	data: Array<T>;
	pagination: { page: number; limit: number; total: number; totalPages: number };
}

/**
 * PD-17095 — one "Possible jobs" suggestion (JQ-4). `key` identifies it within
 * its source (the lower-cased job name, the ship-to's address id or the price
 * contract id); `clientId` is null for a contract, whose customer the rep picks.
 */
export interface JobSuggestionDto {
	source: JobSuggestionSource;
	key: string;
	clientId: number | null;
	clientName: string | null;
	/** The suggested job name. */
	title: string;
	/** Where it comes from, in words ("Ship-to 123 Main St, Detroit"). */
	detail: string | null;
	orderCount: number;
	total: number;
	lastOrderDate: string | null;
	clientAddressId: number | null;
	priceContractId: number | null;
}

/** POST /api/jobs/suggestions/dismiss */
export interface DismissJobSuggestionRequestDto {
	source: JobSuggestionSource;
	key: string;
	clientId: number | null;
}

/** POST /api/jobs/:id/apply-suggestion — link what the suggestion names to the job. */
export interface ApplyJobSuggestionRequestDto {
	source: JobSuggestionSource;
	key: string;
}

/** PD-17109 — the job an opportunity is on, as the opportunity page and the Sales Helper show it. */
export interface OpportunityJobSummaryDto {
	id: number;
	name: string;
	jobNumber: string | null;
	status: JobStatus;
	/** The job's project (an opportunity on the job inherits nothing from it automatically). */
	projectId: number | null;
	projectName: string | null;
}

/** GET /api/opportunities/:id/job */
export interface OpportunityJobDto {
	opportunityId: number;
	opportunityName: string;
	clientId: number;
	amount: number | null;
	/** The opportunity's own project. */
	projectId: number | null;
	/** The opportunity's linked quote or order. */
	orderId: number | null;
	job: OpportunityJobSummaryDto | null;
	/**
	 * On a link response only: whether the opportunity's quote or order went
	 * on the job too (not when it is on another job, or the caller cannot edit jobs).
	 */
	orderOnJob?: boolean;
}

/** PUT /api/opportunities/:id/job */
export interface LinkOpportunityJobRequestDto {
	jobId: number;
	/** Move it from the job it is on now; without it that is refused (409). */
	move?: boolean;
}

/** GET /api/jobs/for-order/:orderId */
export interface OrderJobDto {
	orderId: number;
	clientId: number;
	job: JobOptionDto | null;
}
